import { defineStore } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { db, storage } from '@/firebase/init'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { ref as storageRef, uploadBytes } from 'firebase/storage'
import { generateFirestoreId } from '@/globals/typescript/utils/utils'
import { resizeImageToMinSize } from '@/globals/typescript/utils/imageResize'
import {
  customerOwnerApi,
  customerOwnerContext,
} from '@/globals/typescript/utils/api/customerOwnerApi'
import {
  Project,
  type IProject,
  FloorPlan,
  type IFloorPlan,
  type TFloorPlanOrigin,
} from '@team-milva/lab-models/resources'
import type { IAddress } from '@team-milva/lab-models'

// "Lab Customer — Projects, Floor Plans & Samples" plan, Phase 6 — real
// Projects/FloorPlans, replacing the deleted mock projectStore (Phase 6a).
//
// Reads go straight to Firestore (loadProjects/loadFloorPlans below), exactly
// like orderStore's realOrders — there is deliberately no
// getProject/listProjects lab-cloud call wrapped here, only the mutating
// endpoints (create/update/archive/restore/addFloorPlanToProject/
// createManualFloorPlan/captureFloorPlanMapbox) and the one derived-computation GET
// (listAvailableFloorPlans, the picker pool — not a plain passthrough list).
// See the plan's CA architecture rule: "writes go through lab-cloud, reads go
// through Firebase directly."
//
// Project and FloorPlan are used directly from @team-milva/lab-models/resources,
// with no local wrapper class in globals/typescript/models/ — mirrors exactly
// how orderStore imports `Order as RealOrder` for the real, Firestore-read
// Orders list. lab-customer's own convention (CLAUDE.md) is to only extend a
// lab-models class when app-specific behavior needs to be added (e.g.
// LabCustomerUserBusiness's termsAccepted) — neither Project nor FloorPlan
// needs anything CA-specific, so a wrapper file would be pure indirection.
export const useProjectStore = defineStore('project', {
  state: () => ({
    // Every one of the current user's Projects, active and archived alike —
    // ProjectsListView.vue filters client-side, same reasoning as realOrders
    // not being pre-filtered by status.
    projects: [] as Project[],
    unsubscribeProjects: null as (() => void) | null,

    // Every one of the current user's FloorPlans. Small per-user collection
    // (one per captured/uploaded floor plan) — loaded in full rather than
    // per-project, so a project's floorPlanIDs can be resolved locally
    // without a second round-trip.
    floorPlans: [] as FloorPlan[],
    unsubscribeFloorPlans: null as (() => void) | null,

    // Shared between CustomerNav.vue's persistent search input and
    // ProjectsListView.vue's filtering, same shape as orderStore.searchQuery
    // (the two are sibling list surfaces with no other shared state). Not
    // reset in resetStore() — a stale query is harmless, same reasoning as
    // orderStore's own comment on this field.
    searchQuery: '',
  }),

  actions: {
    setSearchQuery(searchQuery: string): void {
      this.searchQuery = searchQuery
    },

    loadProjects(): Promise<Project[]> {
      if (this.projectsLoaded) {
        return Promise.resolve(this.projects)
      }

      const currentUser = useUserStore().currentUser

      return new Promise((resolve) => {
        if (!currentUser) {
          return resolve([])
        }

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'Projects'),
          where('createdByUserID', '==', currentUser.id)
        )

        let resolved = false
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const projects: Project[] = []
          querySnapshot.forEach((docSnap) => {
            projects.push(new Project(docSnap.data() as IProject))
          })
          this.projects = projects
          this.unsubscribeProjects = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(projects)
          }
        })
      })
    },

    loadFloorPlans(): Promise<FloorPlan[]> {
      if (this.floorPlansLoaded) {
        return Promise.resolve(this.floorPlans)
      }

      const currentUser = useUserStore().currentUser

      return new Promise((resolve) => {
        if (!currentUser) {
          return resolve([])
        }

        const q = query(
          collection(db, 'Apps', '-lab-customer-', 'FloorPlans'),
          where('createdByUserID', '==', currentUser.id)
        )

        let resolved = false
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const floorPlans: FloorPlan[] = []
          querySnapshot.forEach((docSnap) => {
            floorPlans.push(new FloorPlan(docSnap.data() as IFloorPlan))
          })
          this.floorPlans = floorPlans
          this.unsubscribeFloorPlans = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(floorPlans)
          }
        })
      })
    },

    async createProject(input: {
      name: string
      address?: IAddress
      caseNumber?: string
      isInsuranceCase?: boolean
      insurancePartnerID?: string
      insuranceNumber?: string
      floorPlanCapture?: { lat: number; lng: number; zoom: number; address: string }
    }): Promise<{ id: string; floorPlanID: string | null }> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can create a project')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/projects',
        {
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
      return result.data
    },

    async updateProject(
      projectID: string,
      input: {
        name?: string
        address?: IAddress
        caseNumber?: string
        isInsuranceCase?: boolean
        insurancePartnerID?: string
        insuranceNumber?: string
      }
    ): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit a project')
      }

      const result = await customerOwnerApi(
        owner,
        'patch',
        '/projects/{projectID}',
        {
          path: {
            projectID,
          },
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async archiveProject(projectID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can archive a project')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/projects/{projectID}/archive',
        {
          path: {
            projectID,
          },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async restoreProject(projectID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can restore a project')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/projects/{projectID}/restore',
        {
          path: {
            projectID,
          },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async addFloorPlanToProject(projectID: string, floorPlanID: string): Promise<void> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can edit a project')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/projects/{projectID}/floor-plans',
        {
          path: {
            projectID,
          },
          body: { floorPlanID },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
    },

    async createManualFloorPlan(input: {
      id: string
      title: string
      contentType: string
      // The order/project's own address at the time of upload — only sent
      // when the caller actually has one (see AddSampleDetailsView.vue's/
      // ProjectDetailView.vue's own call sites). A floor-plan library
      // shouldn't need every entry addressed to tell them apart forever,
      // but it costs nothing to snapshot it when it's already known.
      address?: string
      // From uploadFloorPlanImage's own resize step — optional since not
      // every caller necessarily has them, but both current call sites do.
      width?: number
      height?: number
    }): Promise<{ id: string }> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can create a floor plan')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/floor-plans',
        {
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
      return result.data
    },

    async captureFloorPlanMapbox(input: {
      lat: number
      lng: number
      zoom: number
      address: string
      markerLat?: number
      markerLng?: number
      viewportWidthPx?: number
    }): Promise<{
      id: string
      title: string
      image: IFloorPlan['image']
      origin: TFloorPlanOrigin
      address: string | null
    }> {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can capture a floor plan')
      }

      const result = await customerOwnerApi(
        owner,
        'post',
        '/floor-plans/capture-mapbox',
        {
          body: input,
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
      return result.data
    },

    async listAvailableFloorPlans(
      orderID: string,
      projectID?: string
    ): Promise<
      {
        id: string
        title: string
        image: IFloorPlan['image']
        origin: TFloorPlanOrigin
        address: string | null
      }[]
    > {
      // A business or an insurance customer's user (MIL-402) — the helper picks the route tree.
      const owner = customerOwnerContext(useUserStore().currentUser)
      if (!owner) {
        throw new Error('Only a business or insurance customer user can list floor plans')
      }

      const result = await customerOwnerApi(
        owner,
        'get',
        '/floor-plans',
        {
          query: { orderID, ...(projectID ? { projectID } : {}) },
        }
      )

      if (!result.success) throw new Error(result.message ?? result.code)
      return result.data.floorPlans
    },

    // Uploads a manually-picked floor-plan image client-direct to Storage,
    // ahead of calling createManualFloorPlan — Bilag-style, id generated
    // client-side first (see firestore-patterns.md), no extension appended:
    // createManualFloorPlanService.ts checks the file exists at exactly
    // `labAccounts/{labAccountID}/floorPlans/{uid}/{id}`, so the upload path
    // must match that exactly (see captureFloorPlanService.ts's matching
    // storage path, and storage.rules' floorPlans block).
    //
    // Resized (never cropped — a floor plan keeps its own aspect ratio) via
    // the same resizeImageToMinSize as PhotoUploadList.vue, matching
    // Sampler's own AddEditFloorplanModal (`:minSize="1200" shape="original"`).
    async uploadFloorPlanImage(
      file: File,
      minSize = 1200
    ): Promise<{ id: string; contentType: string; path: string; width: number; height: number }> {
      const currentUser = useUserStore().currentUser
      if (!currentUser) {
        throw new Error('Not logged in')
      }

      const id = generateFirestoreId()
      const contentType = file.type || 'image/jpeg'
      const path = `labAccounts/${currentUser.labAccountID}/floorPlans/${currentUser.id}/${id}`

      const { blob, width, height } = await resizeImageToMinSize(file, minSize)

      await uploadBytes(storageRef(storage, path), blob, { contentType })

      return { id, contentType, path, width, height }
    },

    resetStore(): void {
      if (this.unsubscribeProjects) {
        this.unsubscribeProjects()
        this.unsubscribeProjects = null
      }
      this.projects = []

      if (this.unsubscribeFloorPlans) {
        this.unsubscribeFloorPlans()
        this.unsubscribeFloorPlans = null
      }
      this.floorPlans = []
    },
  },

  getters: {
    projectsLoaded: (state): boolean => !!state.unsubscribeProjects,
    floorPlansLoaded: (state): boolean => !!state.unsubscribeFloorPlans,

    sortedProjects: (state): Project[] =>
      [...state.projects].sort((a, b) => b.timestamps.createdAt - a.timestamps.createdAt),

    activeProjects(): Project[] {
      return this.sortedProjects.filter((x) => x.activeState === 'active')
    },
    archivedProjects(): Project[] {
      return this.sortedProjects.filter((x) => x.activeState === 'archived')
    },

    getProjectById:
      (state) =>
      (projectID: string): Project | undefined =>
        state.projects.find((x) => x.id === projectID),

    getFloorPlanById:
      (state) =>
      (floorPlanID: string): FloorPlan | undefined =>
        state.floorPlans.find((x) => x.id === floorPlanID),

    // The floor plans a given project's own floorPlanIDs currently resolve
    // to — the id list is a convenience/association, not exclusive
    // ownership (see the plan's Decisions Made), so a stale id that no
    // longer resolves is silently dropped rather than surfaced.
    getFloorPlansForProject:
      (state) =>
      (project: Project): FloorPlan[] =>
        project.floorPlanIDs
          .map((id) => state.floorPlans.find((x) => x.id === id))
          .filter((x): x is FloorPlan => !!x),
  },
})
